// Claude AI categorisation — uses Anthropic API via proxy
// Suggests HMRC category for a transaction based on description + merchant

const SYSTEM_PROMPT = `You are a UK tax categorisation assistant built into Quarterly, an MTD ITSA app.

Your job is to suggest the correct HMRC expense or income category for a transaction.

Available categories:
INCOME:
- income_freelance: Freelance / Consulting income
- income_sales: Product Sales
- income_other: Other Income
- income_rental: Rental Income

EXPENSES:
- exp_software: Software & Subscriptions
- exp_travel: Travel & Fuel
- exp_meals: Meals & Entertainment (HMRC 50% rule applies)
- exp_equipment: Office & Equipment
- exp_marketing: Marketing & Advertising
- exp_professional: Professional Services
- exp_training: Training & Development
- exp_bank: Bank Charges & Fees
- exp_rent: Rent & Premises
- exp_insurance: Insurance
- exp_legal: Legal & Compliance
- exp_stock: Stock & Materials

PERSONAL:
- personal: Personal expense — exclude from tax

Respond ONLY with valid JSON in this exact format:
{
  "category": "category_id_here",
  "confidence": 0.95,
  "reason": "One sentence explaining why this HMRC category applies and any relevant rule."
}

Be accurate. If it looks personal (supermarket, clothing, personal subscriptions), say personal.
Confidence should reflect how certain you are (0.0 to 1.0).`

export async function getClaudeCategory(transaction, merchantRules = {}) {
  // Check merchant memory first — instant return, no API call needed
  const merchantKey = (transaction.merchant || transaction.description || '').toLowerCase().trim()
  if (merchantKey && merchantRules[merchantKey]) {
    const remembered = merchantRules[merchantKey]
    return {
      category:   remembered.category,
      confidence: 0.97,
      reason:     `Categorised based on your previous choice for ${transaction.merchant || transaction.description}.`,
      source:     'memory'
    }
  }

  // Build context from merchant history to help AI learn your patterns
  const historyContext = Object.keys(merchantRules).length > 0
    ? `\n\nThis user's past categorisations (use as context):\n${
        Object.entries(merchantRules).slice(0, 15)
          .map(([m, r]) => `- "${m}" → ${r.category}`)
          .join('\n')
      }`
    : ''

  const prompt = `Transaction to categorise:
Description: ${transaction.description}
Merchant: ${transaction.merchant || 'Unknown'}
Amount: £${transaction.amount}
Type: ${transaction.type} (${transaction.type === 'income' ? 'money coming in' : 'money going out'})${historyContext}`

  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model: 'claude-haiku-4-5-20251001',
        max_tokens: 200,
        system: SYSTEM_PROMPT,
        messages: [{ role: 'user', content: prompt }]
      })
    })

    if (!response.ok) return null

    const data = await response.json()
    const text = data.content?.[0]?.text || ''

    // Parse JSON from response
    const match = text.match(/\{[\s\S]*\}/)
    if (!match) return null

    const parsed = JSON.parse(match[0])
    return {
      category:   parsed.category,
      confidence: parsed.confidence,
      reason:     parsed.reason,
      source:     'claude'
    }
  } catch (e) {
    return null
  }
}
